import {
  CModal,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useReducer, useState } from "react";
import BoxWrapper from "../../components/BoxWrapper";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/Pagination";
import RenderTable from "../../components/RenderTable";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import {
  PartnerRequestBadge,
  usePartnerRequestAlerts,
} from "../../hooks/Partners/usePartnerRequestAlerts";
import { usePartnersQuery } from "../../hooks/Partners/usePartnersQuery";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import NewPartner from "./NewPartner";
import PartnerRequests from "./PartnerRequests";
import PartnersTable from "./PartnerTable";
import PartnerAccounts from "./PartnerAccounts";

const PAGE_SIZE = 10;

export default function Partners() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    if (window.location.hash === "#accounts") return 2;
    if (window.location.hash === "#partner") return 1;
    return 0;
  });
  const [visible, setVisible] = useState(false);
  const {
    total: requestTotal,
    hasNew: hasNewRequests,
    markSeen,
  } = usePartnerRequestAlerts();

  const { Partners, fetchingPartners, deletePartner, mutating } =
    usePartnersQuery();

  useEffect(() => {
    if (activeTab === 1) markSeen();
  }, [activeTab, markSeen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const paginatedContent = usePaginatedContent(
    currentPage,
    PAGE_SIZE,
    Partners,
  );

  const filteredPartners = useMemo(() => {
    const Partners = paginatedContent?.filter(
      (Partner: any) =>
        Partner?.name?.toLowerCase()?.includes(searchQuery) ||
        Partner?.link?.toLowerCase()?.includes(searchQuery),
    );
    return Partners;
  }, [paginatedContent, searchQuery]);

  const [PartnersToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const handleBulkDelete = () => {
    PartnersToBeDeleted.ids.forEach((id) => {
      if (!id) return;
      deletePartner(id);
      PartnersToBeDeleted.ids = [];
    });
  };
  const PartnerSVG = () => {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="white"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.7044 3.51898C10.034 3.51898 9.46373 3.9848 9.30365 4.61265H14.6863C14.5263 3.9848 13.956 3.51898 13.2856 3.51898H10.7044ZM16.2071 4.61264H18.1881C20.2891 4.61264 22 6.34428 22 8.47085C22 8.47085 21.94 9.3711 21.92 10.6248C21.918 10.724 21.8699 10.8212 21.7909 10.88C21.3097 11.2354 20.8694 11.5291 20.8294 11.5493C19.1686 12.6632 17.2386 13.447 15.1826 13.8369C15.0485 13.8632 14.9165 13.7934 14.8484 13.6739C14.2721 12.6754 13.1956 12.0253 11.995 12.0253C10.8024 12.0253 9.71586 12.6683 9.12256 13.6678C9.05353 13.7853 8.92346 13.8531 8.7904 13.8278C6.75138 13.4369 4.82141 12.6541 3.17059 11.5594L2.21011 10.8911C2.13007 10.8405 2.08004 10.7493 2.08004 10.6481C2.05003 10.1316 2 8.47085 2 8.47085C2 6.34428 3.71086 4.61264 5.81191 4.61264H7.78289C7.97299 3.1443 9.2036 2 10.7044 2H13.2856C14.7864 2 16.017 3.1443 16.2071 4.61264ZM21.6598 12.8152L21.6198 12.8355C19.5988 14.1924 17.1676 15.0937 14.6163 15.4684C14.2561 15.519 13.8959 15.2861 13.7959 14.9216C13.5758 14.0912 12.8654 13.5443 12.015 13.5443H12.005H11.985C11.1346 13.5443 10.4242 14.0912 10.2041 14.9216C10.1041 15.2861 9.74387 15.519 9.38369 15.4684C6.83242 15.0937 4.4012 14.1924 2.38019 12.8355C2.37019 12.8254 2.27014 12.7646 2.1901 12.8152C2.10005 12.8659 2.10005 12.9874 2.10005 12.9874L2.17009 18.1519C2.17009 20.2785 3.87094 22 5.97199 22H18.018C20.1191 22 21.8199 20.2785 21.8199 18.1519L21.9 12.9874C21.9 12.9874 21.9 12.8659 21.8099 12.8152C21.7599 12.7849 21.6999 12.795 21.6598 12.8152ZM12.7454 17.0583C12.7454 17.4836 12.4152 17.8177 11.995 17.8177C11.5848 17.8177 11.2446 17.4836 11.2446 17.0583V15.7519C11.2446 15.3367 11.5848 14.9924 11.995 14.9924C12.4152 14.9924 12.7454 15.3367 12.7454 15.7519V17.0583Z"
        />{" "}
      </svg>
    );
  };

  const totalPages = Math.ceil(Partners?.length / PAGE_SIZE);
  return (
    <AdminRoute>
      <h5 className="fs-4 fw-bold mb-2">Partenaires</h5>
      <BoxWrapper>
        <CNav variant="tabs" className="mb-2">
          <CNavItem
            href="#partners"
            active={activeTab === 0}
            onClick={() => setActiveTab(0)}
          >
            <CNavLink>Partenaires</CNavLink>
          </CNavItem>
          <CNavItem
            href="#partner"
            active={activeTab === 1}
            onClick={() => setActiveTab(1)}
          >
            <CNavLink className="d-flex align-items-center">
              Demandes de partenariat
              <PartnerRequestBadge
                total={requestTotal}
                hasNew={hasNewRequests}
              />
            </CNavLink>
          </CNavItem>
          <CNavItem
            href="#accounts"
            active={activeTab === 2}
            onClick={() => setActiveTab(2)}
          >
            <CNavLink>Comptes partenaires</CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent>
          <CTabPane visible={activeTab === 0}>
            <div
              className="d-flex  justify-content-between align-items-baseline before-table"
              id="partners"
            >
              <div className="d-flex align-items-center gap-2 p-3 delete-add">
                <button
                  onClick={() => setVisible(!visible)}
                  className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
                >
                  + Ajouter un partenaire
                </button>
                <CModal
                  size="lg"
                  alignment="center"
                  visible={visible}
                  onClose={() => setVisible(false)}
                >
                  <NewPartner
                    onAccept={() => {}}
                    onClose={() => setVisible(false)}
                  />
                </CModal>

                <button
                  disabled={mutating || PartnersToBeDeleted?.ids.length === 0}
                  onClick={handleBulkDelete}
                  className="outline-none  btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
                >
                  {!mutating ? (
                    <>
                      <DeleteIcon fill={colors.primary} /> Supprimer
                    </>
                  ) : (
                    <>
                      <CSpinner size="sm" /> {general.fr.wait}
                    </>
                  )}
                </button>
              </div>
              <div className="search-form">
                <div className=" input-field">
                  <MagnifyingGlassIcon color="gray" width={20} />
                  <input
                    type="text"
                    className="w-50 border-0  outline-none rounded-sm p-1 text-gray fs-6"
                    placeholder="Rechercher"
                    onChange={(e) =>
                      setSearchQuery(e?.target?.value?.toLowerCase())
                    }
                  />
                </div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
                style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
              >
                <PartnerSVG />
                <p className="fs-5 fw-bold m-0  text-white text-pre">
                  {" "}
                  {Partners?.length}{" "}
                  {Partners?.length > 1 ? "Partenaires" : "Partenaire"}
                </p>
              </div>
            </div>

            <RenderTable
              loading={fetchingPartners}
              render={() =>
                filteredPartners?.length > 0 ? (
                  <PartnersTable
                    Partners={filteredPartners as Partner[]}
                    PartnersToBeDeleted={dispatch}
                  />
                ) : (
                  <p className="text-center fs-5 my-2">{general.fr.noData}</p>
                )
              }
            />
          </CTabPane>
          <CTabPane visible={activeTab === 1}>
            <PartnerRequests />
          </CTabPane>
          <CTabPane visible={activeTab === 2}>
            {activeTab === 2 && <PartnerAccounts />}
          </CTabPane>
        </CTabContent>
      </BoxWrapper>
      {activeTab === 0 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          siblingCount={0}
          totalCount={totalPages}
          pageSize={PAGE_SIZE}
        />
      )}
    </AdminRoute>
  );
}
